import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import AlertHighlight from "@/components/AlertHighlight";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, ArrowRight, FolderKanban, Sparkles, ShieldCheck, Bell, Beaker, TrendingUp, ScanLine, ExternalLink } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [m, p, a] = await Promise.all([
          api.get("/dashboard/metrics"),
          api.get("/devices"),
          api.get("/alerts/recent-widget"),
        ]);
        setMetrics(m.data);
        setProjects(p.data);
        setAlerts(a.data);
        // First-time user: no devices AND onboarding not completed → offer the tour.
        if (p.data.length === 0 && !localStorage.getItem("vq_onboarded")) {
          navigate("/onboarding", { replace: true });
        }
      } catch (e) {
        logger.error("Dashboard load failed:", e);
      }
    })();
  }, []);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="vq-overline mb-2">Overview · Safety by design</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
              Welcome back, {user?.name?.split(" ")[0] || "Doctor"}.
            </h1>
            <p className="text-slate-600 mt-2">Compliance is cheapest when it&apos;s earliest — here&apos;s where every one of your active projects stands today.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/ocr" data-testid="dashboard-ocr-btn">
              <Button variant="outline" className="h-11 border-slate-300 font-semibold">
                <ScanLine className="w-4 h-4 mr-2" /> OCR Scan
              </Button>
            </Link>
            <Link to="/devices/new" data-testid="dashboard-new-project-btn">
              <Button className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold rounded-md">
                <Plus className="w-4 h-4 mr-2" /> New Device Project
              </Button>
            </Link>
          </div>
        </div>

        {/* Weekly AI-highlighted top alert (dismissable per ISO week). */}
        <AlertHighlight />

        {/* Metric cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <MetricCard
            label="Active Projects"
            value={metrics?.active_projects ?? "—"}
            icon={FolderKanban}
            testId="metric-active-projects"
          />
          <MetricCard
            label="QMS Generated"
            value={metrics?.qms_generated ?? "—"}
            icon={ShieldCheck}
            hint="Ready for submission"
            testId="metric-qms-generated"
          />
          <MetricCard
            label="Compliance Score"
            value={metrics?.compliance_score ? `${metrics.compliance_score}%` : "—"}
            icon={TrendingUp}
            accent
            testId="metric-compliance"
          />
          <MetricCard
            label="New Alerts"
            value={metrics?.alerts ?? "—"}
            icon={Bell}
            hint="Live regulatory feed"
            testId="metric-alerts"
          />
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Projects */}
          <div className="lg:col-span-2 vq-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="vq-overline">Projects</div>
                <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Your device pipeline</h2>
              </div>
              <Link to="/devices" data-testid="view-all-projects-link" className="text-sm font-semibold text-[#0055FF] hover:underline">
                View all
              </Link>
            </div>
            {projects.length === 0 ? (
              <EmptyProjects />
            ) : (
              <div className="space-y-3">
                {projects.slice(0, 4).map((p) => (
                  <Link key={p.project_id} to={`/devices/${p.project_id}`} className="block border border-slate-200 rounded-md p-4 hover:border-slate-400 transition-colors">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-mono text-[10px] text-slate-500 uppercase tracking-wider">{p.project_id}</div>
                        <div className="font-display font-bold text-slate-900 truncate">{p.device_name}</div>
                        <div className="text-xs text-slate-500 mt-1 truncate">{p.intended_use}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusPill status={p.status} />
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar column */}
          <div className="space-y-6">
            {/* VitaCyt POC card */}
            <div className="vq-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-900 rounded flex items-center justify-center">
                  <Beaker className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="vq-overline">Connected</div>
                  <div className="font-display font-bold text-slate-900">VitaCyt POC</div>
                </div>
              </div>
              <div className="text-sm text-slate-600 mb-4">
                Status: <span className="font-semibold text-slate-900">{metrics?.vitacyt_status || "Ready"}</span>
              </div>
              <Link to="/vitacyt" data-testid="dashboard-vitacyt-link">
                <Button variant="outline" className="w-full border-slate-300 font-semibold">
                  Open VitaCyt data
                </Button>
              </Link>
            </div>

            {/* Alerts widget — top 3 by AI relevance in the last 30 days.
                Falls back to newest when triage hasn't populated yet. */}
            <div className="vq-card p-6" data-testid="dashboard-alerts-widget">
              <div className="flex items-center justify-between mb-1">
                <div className="vq-overline">Top by AI relevance · 30d</div>
                <Link to="/alerts" data-testid="dashboard-alerts-view-all" className="text-xs font-semibold text-[#0055FF] hover:underline">
                  View all →
                </Link>
              </div>
              <h3 className="font-display font-bold text-slate-900 mb-4">Regulatory Alerts</h3>
              {alerts.length === 0 ? (
                <p className="text-sm text-slate-500">No alerts yet — the regulatory feed will populate on the next 30-minute poll cycle.</p>
              ) : (
                <div className="space-y-4">
                  {alerts.map((a) => {
                    const score = a.ai_triage?.relevance_score;
                    let pillCls = "bg-slate-50 text-slate-500 border-slate-200";
                    if (typeof score === "number") {
                      if (score >= 80) pillCls = "bg-emerald-50 text-emerald-800 border-emerald-300";
                      else if (score >= 50) pillCls = "bg-blue-50 text-blue-800 border-blue-300";
                      else if (score >= 20) pillCls = "bg-slate-100 text-slate-700 border-slate-300";
                    }
                    return (
                      <div
                        key={a.alert_id}
                        data-testid={`dashboard-alert-${a.alert_id}`}
                        className="border-b border-slate-100 last:border-0 pb-3 last:pb-0"
                      >
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <SeverityBadge severity={a.severity} />
                          <span className="text-[10px] font-mono text-slate-500 uppercase">{a.source}</span>
                          {typeof score === "number" && (
                            <span
                              data-testid={`dashboard-alert-triage-${a.alert_id}`}
                              title={a.ai_triage?.rationale || ""}
                              className={`ml-auto inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${pillCls}`}
                            >
                              <Sparkles className="w-2.5 h-2.5" /> AI {score}
                            </span>
                          )}
                        </div>
                        <Link
                          to="/alerts"
                          className="block text-sm text-slate-900 font-medium line-clamp-2 hover:text-[#0055FF]"
                        >
                          {a.title}
                        </Link>
                        {a.link && (
                          <a
                            href={a.link}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono text-slate-500 hover:text-[#0055FF]"
                          >
                            source <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function MetricCard({ label, value, icon: Icon, hint, accent, testId }) {
  return (
    <div data-testid={testId} className={`vq-card p-5 ${accent ? "bg-slate-900 text-white border-slate-900" : ""}`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`vq-overline ${accent ? "text-slate-400" : ""}`}>{label}</div>
        <Icon className={`w-4 h-4 ${accent ? "text-[#0055FF]" : "text-slate-400"}`} />
      </div>
      <div className={`font-display font-extrabold text-3xl tracking-tight ${accent ? "text-white" : "text-slate-900"}`}>
        {value}
      </div>
      {hint && <div className={`text-xs mt-2 ${accent ? "text-slate-400" : "text-slate-500"}`}>{hint}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    classified: { label: "Classified", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    qms_generated: { label: "QMS Ready", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  };
  const s = map[status] || map.draft;
  return <span className={`px-2 py-1 rounded text-[10px] font-mono border ${s.cls}`}>{s.label}</span>;
}

function SeverityBadge({ severity }) {
  const cls = severity === "high" ? "vq-badge-high" : severity === "medium" ? "vq-badge-med" : "vq-badge-low";
  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border ${cls}`}>{severity}</span>;
}

function EmptyProjects() {
  return (
    <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-md">
      <Sparkles className="w-8 h-8 text-slate-400 mx-auto mb-4" />
      <h3 className="font-display font-bold text-slate-900 mb-1">Start your first project</h3>
      <p className="text-sm text-slate-600 mb-4">Or import the VitaCyt POC to see a full example.</p>
      <div className="flex justify-center gap-3">
        <Link to="/devices/new">
          <Button className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> New Device
          </Button>
        </Link>
        <Link to="/vitacyt">
          <Button variant="outline" className="border-slate-300 font-semibold">Import VitaCyt POC</Button>
        </Link>
      </div>
    </div>
  );
}
